import type { HTMLAttributes, ReactNode } from 'react';
import { cssVars, cx } from '@/lib/cx';
import s from './Grid.module.css';

type Space = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
const space = (n: Space | undefined) => (n == null ? undefined : `var(--space-${n})`);

/** Responsive grid of cards: as many columns of at least `min` as the container fits. */
export function Grid({
  min = '280px',
  gap,
  fit,
  className,
  children,
  as: Tag = 'div',
}: {
  min?: string;
  gap?: Space;
  /** auto-fit (stretch few items) instead of auto-fill (keep column width). */
  fit?: boolean;
  className?: string;
  children: ReactNode;
  as?: 'div' | 'section' | 'ul';
}) {
  return (
    <Tag className={cx(s.grid, fit && s.fit, className)} style={cssVars({ '--grid-min': min, '--grid-gap': space(gap) })}>
      {children}
    </Tag>
  );
}

export function GridItem({ span, children, className }: { span?: 2 | 'all'; children: ReactNode; className?: string }) {
  return <div className={cx(span === 2 && s.span2, span === 'all' && s.spanAll, className)}>{children}</div>;
}

export function Stack({
  gap,
  className,
  children,
  as: Tag = 'div',
  ...rest
}: { gap?: Space; as?: 'div' | 'section' | 'ul' | 'ol' | 'form' } & HTMLAttributes<HTMLElement>) {
  return (
    <Tag className={cx(s.stack, className)} style={cssVars({ '--stack-gap': space(gap) })} {...rest}>
      {children}
    </Tag>
  );
}

export function Cluster({
  gap,
  align,
  justify,
  nowrap,
  className,
  children,
}: {
  gap?: Space;
  align?: 'start' | 'center' | 'end' | 'baseline' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'between';
  nowrap?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const j = justify === 'between' ? 'space-between' : justify === 'start' ? 'flex-start' : justify === 'end' ? 'flex-end' : justify;
  const a = align === 'start' ? 'flex-start' : align === 'end' ? 'flex-end' : align;
  return (
    <div
      className={cx(s.cluster, nowrap && s.nowrap, className)}
      style={cssVars({ '--cluster-gap': space(gap), '--cluster-align': a, '--cluster-justify': j })}
    >
      {children}
    </div>
  );
}
